import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../../services/api";
import {
  COMMERCIAL_STATUS_META,
  type CommercialStatus,
  type User,
} from "../../../types/user";
import { BackButton } from "../../../components/button/back-button";
import { useToast } from "../../../contexts/toast/toast-context";
import {
  COUNTRY_CODE_OPTIONS,
  formatLocalPhone,
  formatPhoneWithCountryCode,
  formatZipCode,
  getApiErrorMessage,
  normalizeDigits,
  normalizePhone,
} from "../../../utils";
import { LoadingButton } from "../../../components/button/loading-button";
import { ClientAttachmentsSection } from "./client-attachments-section";

type AiContextRow = {
  id: string;
  key: string;
  value: string;
};

function formatDateForInput(value?: Date | string) {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function createAiContextRowId() {
  return `ai-context-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function createAiContextRows(aiContext?: Record<string, string>) {
  return Object.entries(aiContext ?? {}).map(([key, value], index) => ({
    id: `ai-context-${index}-${key}`,
    key,
    value: String(value ?? ""),
  }));
}

export default function CadastroCliente() {
  const { error, success } = useToast();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [countryCode, setCountryCode] = useState(COUNTRY_CODE_OPTIONS[0].value);

  const [user, setUser] = useState<Partial<User>>({
    userName: "",
    dialCode: "",
    additionalInfo: "",
    acceptsEmailCommunications: false,
    aiContext: {},
    commercialStatus: "UNQUALIFIED",
  });
  const [aiContextRows, setAiContextRows] = useState<AiContextRow[]>([]);
  const [newAiContextRow, setNewAiContextRow] = useState({
    key: "",
    value: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (id) {
      try {
        api.get<User>(`/user/${id}`).then((res) => {
          const userData = res.data;
          setUser({
            ...userData,
            zipCode: formatZipCode(userData.zipCode || ""),
            dialCode: formatLocalPhone(
              userData.dialCode || userData.userPhone || "",
              userData.countryCode,
            ),
          });
          setAiContextRows(createAiContextRows(userData.aiContext));
          setCountryCode(userData.countryCode || COUNTRY_CODE_OPTIONS[0].value);
        });
      } catch (err) {
        const apiMessage = getApiErrorMessage(err);
        error(apiMessage);
      }
    }
  }, [id, error]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;
    const checked =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : false;

    if (name === "dialCode") {
      setUser({ ...user, dialCode: formatLocalPhone(value, countryCode) });
      return;
    }

    if (name === "zipCode") {
      setUser({ ...user, zipCode: formatZipCode(value) });
      return;
    }

    if (type === "checkbox") {
      setUser({ ...user, [name]: checked });
      return;
    }

    setUser({ ...user, [name]: value });
  };

  const getAiContextRowsForPayload = () => {
    const pendingRow = {
      id: "pending-ai-context-row",
      key: newAiContextRow.key,
      value: newAiContextRow.value,
    };

    return [...aiContextRows, pendingRow].filter(
      (row) => row.key.trim() || row.value.trim(),
    );
  };

  const serializeAiContextRows = () =>
    Object.fromEntries(
      getAiContextRowsForPayload()
        .filter((row) => row.key.trim() && row.value.trim())
        .map((row) => [row.key.trim(), row.value.trim()]),
    );

  const getAiContextValidationError = () => {
    const rows = getAiContextRowsForPayload();

    if (rows.some((row) => !row.key.trim())) {
      return "Informe a chave de todos os itens da memória IA.";
    }

    if (rows.some((row) => !row.value.trim())) {
      return "Informe o valor de todos os itens do memória IA.";
    }

    const keys = rows.map((row) => row.key.trim());
    const uniqueKeys = new Set(keys);

    if (uniqueKeys.size !== keys.length) {
      return "Não é possível salvar chaves duplicadas no memória IA.";
    }

    return "";
  };

  const buildPayload = () => {
    const payload = {
      userName: user.userName,
      dialCode: normalizePhone(user.dialCode || ""),
      countryCode,
      additionalInfo: user.additionalInfo,
      birthDate: user.birthDate || "",
      email: user.email || "",
      zipCode: normalizeDigits(user.zipCode || ""),
      addressLine: user.addressLine || "",
      addressNumber: user.addressNumber || "",
      complement: user.complement || "",
      neighborhood: user.neighborhood || "",
      city: user.city || "",
      country: user.country || "",
      acceptsEmailCommunications: Boolean(user.acceptsEmailCommunications),
      commercialStatus: user.commercialStatus ?? "UNQUALIFIED",
    };

    return {
      ...payload,
      aiContext: serializeAiContextRows(),
    };
  };

  const handleAiContextRowChange = (
    rowId: string,
    field: "key" | "value",
    value: string,
  ) => {
    setAiContextRows((currentRows) =>
      currentRows.map((row) =>
        row.id === rowId ? { ...row, [field]: value } : row,
      ),
    );
  };

  const handleRemoveAiContextRow = (rowId: string) => {
    setAiContextRows((currentRows) =>
      currentRows.filter((row) => row.id !== rowId),
    );
  };

  const handleAddAiContextRow = () => {
    const trimmedKey = newAiContextRow.key.trim();
    const trimmedValue = newAiContextRow.value.trim();

    if (!trimmedKey || !trimmedValue) {
      error("Informe chave e valor para adicionar à memória IA.");
      return;
    }

    const alreadyExists = aiContextRows.some(
      (row) => row.key.trim() === trimmedKey,
    );

    if (alreadyExists) {
      error("Já existe um item com essa chave à memória IA.");
      return;
    }

    setAiContextRows((currentRows) => [
      ...currentRows,
      {
        id: createAiContextRowId(),
        key: trimmedKey,
        value: trimmedValue,
      },
    ]);
    setNewAiContextRow({ key: "", value: "" });
  };

  const handleUserChangeFromAttachments = (updatedUser: User) => {
    setUser({
      ...updatedUser,
      dialCode: formatLocalPhone(
        updatedUser.dialCode || updatedUser.userPhone || "",
        updatedUser.countryCode,
      ),
    });
    setCountryCode(updatedUser.countryCode || COUNTRY_CODE_OPTIONS[0].value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    try {
      const normalizedPhone = normalizePhone(user.dialCode || "");
      if (normalizedPhone.length < 10 || normalizedPhone.length > 15) {
        error("Telefone inválido.");
        return;
      }
      const aiContextValidationError = getAiContextValidationError();
      if (aiContextValidationError) {
        error(aiContextValidationError);
        return;
      }
      setIsSubmitting(true);
      if (id) {
        await api.patch(`/user/${id}`, buildPayload());
        success("Contato atualizado com sucesso!");
      } else {
        await api.post("/user", buildPayload());
        success("Contato cadastrado com sucesso!");
      }

      navigate("/painel/contatos");
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="app-page">
      <BackButton />
      <h1 className="app-page-title mb-6">
        {id ? "Editar Contato" : "Novo Contato"}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="form-label">Nome</label>
            <input
              type="text"
              name="userName"
              value={user.userName || ""}
              onChange={handleChange}
              className="form-input"
              required
            />
          </div>
          <div>
            <label className="form-label">Data de Nascimento</label>
            <input
              type="date"
              name="birthDate"
              value={formatDateForInput(user.birthDate)}
              onChange={handleChange}
              className="form-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4">
          <div>
            <label className="form-label">Código do país</label>
            <select
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              className="form-input"
            >
              {COUNTRY_CODE_OPTIONS.map((code) => (
                <option key={code.value} value={code.value}>
                  {code.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">Telefone</label>
            <input
              type="text"
              name="dialCode"
              value={user.dialCode || ""}
              onChange={handleChange}
              inputMode="numeric"
              className="form-input"
              required
            />
            <p className="mt-2 text-xs text-slate-500">
              Formato final:{" "}
              {formatPhoneWithCountryCode(user.dialCode || "", countryCode) ||
                "-"}
            </p>
          </div>
        </div>

        <div>
          <label className="form-label">Email</label>
          <input
            type="email"
            name="email"
            value={user.email || ""}
            onChange={handleChange}
            className="form-input"
          />
        </div>

        <div>
          <label className="form-label" htmlFor="commercialStatus">
            Status comercial
          </label>
          <select
            id="commercialStatus"
            name="commercialStatus"
            value={user.commercialStatus ?? "UNQUALIFIED"}
            onChange={handleChange}
            className="form-input"
          >
            {(Object.keys(COMMERCIAL_STATUS_META) as CommercialStatus[]).map(
              (status) => (
                <option key={status} value={status}>
                  {COMMERCIAL_STATUS_META[status].label}
                </option>
              ),
            )}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="form-label">Cep</label>
            <input
              type="text"
              name="zipCode"
              value={user.zipCode || ""}
              onChange={handleChange}
              inputMode="numeric"
              maxLength={9}
              className="form-input"
            />
          </div>
          <div>
            <label className="form-label">Cidade</label>
            <input
              type="text"
              name="city"
              value={user.city || ""}
              onChange={handleChange}
              className="form-input"
            />
          </div>
          <div>
            <label className="form-label">País</label>
            <input
              type="text"
              name="country"
              value={user.country || ""}
              onChange={handleChange}
              className="form-input"
            />
          </div>
        </div>

        <div>
          <div>
            <label className="form-label">Logradouro</label>
            <input
              type="text"
              name="addressLine"
              value={user.addressLine || ""}
              onChange={handleChange}
              className="form-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="form-label">Número</label>
            <input
              type="text"
              name="addressNumber"
              value={user.addressNumber || ""}
              onChange={handleChange}
              className="form-input"
            />
          </div>
          <div>
            <label className="form-label">Complemento</label>
            <input
              type="text"
              name="complement"
              value={user.complement || ""}
              onChange={handleChange}
              className="form-input"
            />
          </div>
          <div>
            <label className="form-label">Bairro</label>
            <input
              type="text"
              name="neighborhood"
              value={user.neighborhood || ""}
              onChange={handleChange}
              className="form-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-700">
            <input
              type="checkbox"
              name="acceptsEmailCommunications"
              checked={Boolean(user.acceptsEmailCommunications)}
              onChange={handleChange}
              className="form-checkbox mt-0.5"
            />
            <span>Termo de aceite de comunicações por Email</span>
          </label>
        </div>

        {id && user.contactConsent && (
          <section className="app-card space-y-3 p-4">
            <h2 className="font-semibold text-slate-900">
              Consentimento de marketing via WhatsApp
            </h2>
            <p className="text-sm text-slate-600">
              Status:{" "}
              {user.contactConsent.status === "GRANTED"
                ? "Aceito"
                : user.contactConsent.status === "DENIED"
                  ? "Recusado"
                  : "Aguardando resposta"}
            </p>
            <button
              type="button"
              className="btn btn-danger"
              onClick={async () => {
                const confirmed = window.confirm(
                  "Excluir o consentimento? O atendimento atual não será alterado. O consentimento será solicitado novamente quando um novo atendimento for aberto.",
                );
                if (!confirmed) return;
                try {
                  await api.delete(`/user/${id}/contact-consent`);
                  setUser((current) => ({
                    ...current,
                    contactConsent: undefined,
                  }));
                  success("Consentimento excluído com sucesso.");
                } catch (err) {
                  error(getApiErrorMessage(err));
                }
              }}
            >
              Excluir consentimento
            </button>
          </section>
        )}

        <div>
          <label className="form-label">Informações adicionais</label>
          <textarea
            name="additionalInfo"
            value={user.additionalInfo || ""}
            onChange={handleChange}
            rows={4}
            className="form-textarea"
          />
        </div>

        <div>
          <label className="form-label">Memória IA</label>
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="hidden grid-cols-[minmax(0,1fr)_minmax(0,2fr)_56px] gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold uppercase text-slate-500 md:grid">
              <span>Chave</span>
              <span>Valor</span>
              <span className="sr-only">Ações</span>
            </div>

            {aiContextRows.length === 0 && (
              <div className="border-b border-slate-200 px-3 py-4 text-sm text-slate-500">
                Sem contexto salvo.
              </div>
            )}

            {aiContextRows.map((row) => (
              <div
                key={row.id}
                className="grid grid-cols-1 gap-2 border-b border-slate-200 p-3 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_56px]"
              >
                <input
                  type="text"
                  value={row.key}
                  onChange={(event) =>
                    handleAiContextRowChange(row.id, "key", event.target.value)
                  }
                  aria-label="Chave da memória IA"
                  placeholder="Chave"
                  className="form-input"
                />
                <input
                  type="text"
                  value={row.value}
                  onChange={(event) =>
                    handleAiContextRowChange(
                      row.id,
                      "value",
                      event.target.value,
                    )
                  }
                  aria-label="Valor da memória IA"
                  placeholder="Valor"
                  className="form-input"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveAiContextRow(row.id)}
                  className="btn-icon-danger md:justify-self-end"
                  aria-label={`Excluir item ${row.key || "da memória IA"}`}
                  title="Excluir item"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}

            <div className="grid grid-cols-1 gap-2 bg-slate-50 p-3 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_56px]">
              <input
                type="text"
                value={newAiContextRow.key}
                onChange={(event) =>
                  setNewAiContextRow((currentRow) => ({
                    ...currentRow,
                    key: event.target.value,
                  }))
                }
                aria-label="Nova chave da memória IA"
                placeholder="Nova chave"
                className="form-input"
              />
              <input
                type="text"
                value={newAiContextRow.value}
                onChange={(event) =>
                  setNewAiContextRow((currentRow) => ({
                    ...currentRow,
                    value: event.target.value,
                  }))
                }
                aria-label="Novo valor da memória IA"
                placeholder="Novo valor"
                className="form-input"
              />
              <button
                type="button"
                onClick={handleAddAiContextRow}
                className="btn-icon-success md:justify-self-end"
                disabled={
                  !newAiContextRow.key.trim() && !newAiContextRow.value.trim()
                }
                aria-label="Adicionar item à memória IA"
                title="Adicionar item"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        <LoadingButton
          type="submit"
          className="btn btn-neutral cursor-pointer"
          isLoading={isSubmitting}
          loadingLabel={id ? "Salvando" : "Cadastrando"}
        >
          {id ? "Salvar Alterações" : "Cadastrar Contato"}
        </LoadingButton>
      </form>

      {id && user.id && (
        <div className="mt-8">
          <ClientAttachmentsSection
            userId={user.id}
            attachments={user.attachments}
            onUserChange={handleUserChangeFromAttachments}
          />
        </div>
      )}
    </div>
  );
}
